import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Button from '../Button/Button';
import { BUTTON_COLORS, BUTTON_SIZES } from '../Button/classes';
import IconDropdown from '../Icon/IconDropdown';
import DropdownChildren from './DropdownChildren';

/**
 * A Dropdown toggles a contextual overlay for displaying a list of links. It
 * is made up of a button and a menu. When the button is clicked (or tabbed to
 * with the keyboard and the enter key is pressed), the menu appears.
 *
 * The menu displays a list of links or buttons (made to look like links).
 *
 * To close the menu a user can either click on the button again, tab away and
 * focus on another element, or click anywhere outside of the component itself.
 */
const Dropdown = (props) => {
  const {
    btnClassName,
    children,
    className,
    closeOnDropdownClick,
    color,
    dropdownMenuClassName,
    id,
    position,
    renderButton,
    size,
    text,
    title,
    icon,
    ariaLabel,
    onOpenStateChange,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownDiv = useRef(null);

  const closeMenu = () => {
    setIsOpen(false);
    onOpenStateChange(false);
  };

  const openMenu = () => {
    setIsOpen(true);
    onOpenStateChange(true);
  };

  const handleOutsideClick = (e) => {
    // ignore clicks on the component itself
    if (dropdownDiv.current.contains(e.target)) {
      return;
    }

    closeMenu();
  };

  const getButtonProps = () => ({
    'aria-haspopup': 'true',
    'aria-expanded': isOpen,
    'aria-label': ariaLabel,
    className: cn('c-dropdown__button', { 'is-active': isOpen }, btnClassName),
    color,
    'data-toggle': 'dropdown',
    id,
    onClick: isOpen ? closeMenu : openMenu,
    size,
  });

  const dropdownClasses = cn('c-dropdown', { 'is-open': isOpen }, className);

  const buttonProps = getButtonProps();

  const button = renderButton ? (
    renderButton(buttonProps)
  ) : (
    <Button {...buttonProps}>
      {text}
      <IconDropdown icon={icon} title={title} />
    </Button>
  );

  return (
    <div className={dropdownClasses} ref={dropdownDiv}>
      {button}
      {isOpen && (
        <DropdownChildren
          closeMenu={closeMenu}
          closeOnDropdownClick={closeOnDropdownClick}
          dropdownMenuClassName={dropdownMenuClassName}
          handleOutsideClick={handleOutsideClick}
          id={id}
          position={position}
        >
          {children}
        </DropdownChildren>
      )}
    </div>
  );
};

export default Dropdown;

Dropdown.propTypes = {
  btnClassName: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  closeOnDropdownClick: PropTypes.bool,
  color: PropTypes.oneOf(BUTTON_COLORS),
  dropdownMenuClassName: PropTypes.string,
  id: PropTypes.string.isRequired,
  position: PropTypes.oneOf(['left', 'right', 'full-width']),
  renderButton: PropTypes.func,
  size: PropTypes.oneOf(BUTTON_SIZES),
  text: (props, propName, componentName) => {
    if (!props.renderButton && !props[propName]) {
      throw new Error(
        `Prop ${propName} is required in ${componentName} if you aren't passing a custom renderButton`
      );
    }
  },
  title: PropTypes.string,
  icon: PropTypes.string,
  ariaLabel: PropTypes.string.isRequired,
  onOpenStateChange: PropTypes.func,
};

Dropdown.defaultProps = {
  btnClassName: '',
  children: null,
  className: '',
  closeOnDropdownClick: true,
  color: null,
  dropdownMenuClassName: '',
  position: 'right',
  renderButton: null,
  size: null,
  text: '',
  title: '',
  icon: '',
  onOpenStateChange: () => {},
};
